import React, { useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
} from '@mui/material';
import { SetFilterModule } from "ag-grid-enterprise";
import { PamGrid, GridConfigMenu, useGridConfigs } from '@1823-partners/core';
import dayjs from 'dayjs';

import api from '../api';

const CusipTables = ({ 
  cashflowData, 
  metricsData, 
  tableLoading, 
  formData,
  showDebugInfo = false,
  tableType = 'both' // 'cashflow', 'metrics', or 'both'
}) => {
  const cashflowGridRef = useRef(null);
  const metricsGridRef = useRef(null);

  const defaultColDef = {
    minWidth: 100,
    width: 130,
    maxWidth: 200,
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    cellClass: 'ag-cell',
    suppressCellFlash: true,
    enableCellTextSelection: true,
    suppressMovable: true,
    suppressSizeToFit: true
  };

  // Domain-specific date formatter (YYYY-MM-DD)
  const isoDateFormatter = (params) => {
    if (!params.value) return '';
    try {
      return dayjs(params.value).format('YYYY-MM-DD');
    } catch {
      return params.value;
    }
  };

  // Domain-specific datetime formatter (YYYY-MM-DD HH:mm:ss)
  const isoDateTimeFormatter = (params) => {
    if (!params.value) return '';
    try {
      return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
    } catch {
      return params.value;
    }
  };

  // Numeric formatter that shows '-' for null/NaN values
  const nullSafeNumberFormatter = (params) => {
    if (params.value === null || params.value === undefined || isNaN(params.value)) return '-';
    return params.value.toLocaleString();
  };

  const cashflowColumnDefs = [
    { headerName: 'As of Date', field: 'ASOFDT', filter: 'agDateColumnFilter', valueFormatter: isoDateFormatter },
    { headerName: 'Currency', field: 'CCY', filter: 'agSetColumnFilter' },
    { headerName: 'Sim Date', field: 'SIM_DATE', filter: 'agDateColumnFilter', valueFormatter: isoDateFormatter },
    { headerName: 'CF Date', field: 'CF_DATE', filter: 'agDateColumnFilter', valueFormatter: isoDateFormatter },
    { headerName: 'Principal', field: 'PRINCIPAL', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Interest', field: 'INTEREST', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Scenario', field: 'SCENARIO', filter: 'agSetColumnFilter' },
    { headerName: 'CUSIP', field: 'CUSIP', filter: 'agSetColumnFilter' },
    { headerName: 'Book', field: 'CW_BOOK', filter: 'agSetColumnFilter' },
    { headerName: 'Lot ID', field: 'LOT_ID', filter: 'agSetColumnFilter' },
    { headerName: 'Run Type', field: 'RUNTYPE', filter: 'agSetColumnFilter' },
    { headerName: 'Config ID', field: 'CONFIG_ID', filter: 'agSetColumnFilter' },
    { headerName: 'Run Date', field: 'RUN_DATE', filter: 'agDateColumnFilter', valueFormatter: isoDateTimeFormatter },
  ];

  const metricsColumnDefs = [
    { headerName: 'CUSIP', field: 'CUSIP', filter: 'agSetColumnFilter' },
    { headerName: 'Lot ID', field: 'LOT_ID', type: 'numericColumn', filter: 'agNumberColumnFilter' },
    { headerName: 'Book', field: 'BOOK', filter: 'agSetColumnFilter' },
    { headerName: 'Tenor', field: 'TENOR', filter: 'agSetColumnFilter' },
    { headerName: 'Date', field: 'DATE', filter: 'agDateColumnFilter', valueFormatter: isoDateFormatter },
    { headerName: 'Curve', field: 'CURVE', filter: 'agSetColumnFilter' },
    { headerName: 'Clean Price', field: 'CLEAN_PX', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Outstanding Balance', field: 'OUTSTANDING_BAL', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Book Value', field: 'BOOK_VALUE', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'ZNPV', field: 'ZNPV', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'PV', field: 'PV', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'GL', field: 'GL', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Accrued Interest', field: 'ACC_INT', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'MOM Accrued Interest', field: 'MOM_ACC_INT', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Amort Premium', field: 'AMORT_PREM', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Market Yield', field: 'MKT_YLD', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Book Yield', field: 'BOOK_YLD', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'BEY', field: 'BEY', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Book BEY', field: 'BOOK_BEY', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Z-Spread', field: 'Z_SPREAD', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'WAL', field: 'WAL', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Duration', field: 'DUR', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Effective Duration', field: 'EFF_DUR', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'IR Gamma', field: 'IR_GAMMA', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Convexity', field: 'CONV', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'DV01', field: 'DV01', type: 'numericColumn', filter: 'agNumberColumnFilter', valueFormatter: nullSafeNumberFormatter },
    { headerName: 'Instrument Type', field: 'INST_TYPE', filter: 'agSetColumnFilter' },
    { headerName: 'Run Type', field: 'RUNTYPE', filter: 'agSetColumnFilter' },
    { headerName: 'Scenario', field: 'SCENARIO', filter: 'agSetColumnFilter' },
    { headerName: 'As of Date', field: 'ASOFDT', filter: 'agDateColumnFilter', valueFormatter: isoDateFormatter },
    { headerName: 'Config ID', field: 'CONFIG_ID', filter: 'agSetColumnFilter' },
    { headerName: 'Run Date', field: 'RUN_DATE', filter: 'agDateColumnFilter', valueFormatter: isoDateTimeFormatter },
  ];

  // Own the view-config hook per grid here (rather than letting
  // GridConfigMenu instantiate it internally) so we can bridge
  // gridConfigs.onGridReady to PamGrid's onGridReady — without that bridge a
  // grid that mounts after the configs response would miss the default-layout
  // auto-apply. Each grid gets a UNIQUE gridId so their saved layouts never
  // clobber one another.
  const cashflowGridConfigs = useGridConfigs(api, {
    app: 'scenarios',
    gridId: 'cashflow',
    gridApiRef: cashflowGridRef,
  });
  const metricsGridConfigs = useGridConfigs(api, {
    app: 'scenarios',
    gridId: 'metrics',
    gridApiRef: metricsGridRef,
  });

  // GridConfigMenu is stateful (dropdown + dialog) and needs the grid ref, so
  // it rides through PamGrid's customToolbarComponents slot.
  const cashflowToolbarComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={cashflowGridConfigs} />,
  ], [cashflowGridConfigs]);
  const metricsToolbarComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={metricsGridConfigs} />,
  ], [metricsGridConfigs]);

  // Determine which table(s) to render based on data and tableType
  const shouldShowCashflow = cashflowData && (tableType === 'cashflow' || tableType === 'both');
  const shouldShowMetrics = metricsData && (tableType === 'metrics' || tableType === 'both');

  return (
    <>
      {/* Cashflow Table */}
      {shouldShowCashflow && (
        <Paper sx={{ p: 2, width: '100%', maxWidth: '100vw', overflow: 'hidden', mb: tableType === 'both' ? 3 : 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Cashflow Data
            </Typography>
          </Box>
          <PamGrid
            ref={cashflowGridRef}
            modules={[SetFilterModule]}
            rowData={cashflowData || []}
            columnDefs={cashflowColumnDefs}
            defaultColDef={defaultColDef}
            isLoading={tableLoading}
            loadingMessage="Loading cashflow data..."
            showToolbar={true}
            title="Cashflow Data"
            showExport={true}
            exportLabel="Export to Excel"
            exportConfig={{ fileName: `cashflow_${formData.cusip}_${dayjs().format('YYYY-MM-DD')}.xlsx`, sheetName: 'Cashflow' }}
            customToolbarComponents={cashflowToolbarComponents}
            onGridReady={cashflowGridConfigs.onGridReady}
            onFilterChanged={cashflowGridConfigs.onGridStateChanged}
            agGridProps={cashflowGridConfigs.stashBindings}
            height={500}
            showQuickFilter={false}
            showFooter={false}
            copyHeadersToClipboard={true}
            suppressHorizontalScroll={false}
            alwaysShowHorizontalScroll={true}
          />
        </Paper>
      )}

      {/* Metrics Table */}
      {shouldShowMetrics && (
        <Paper sx={{ p: 2, width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Metrics Data
            </Typography>
          </Box>
          <PamGrid
            ref={metricsGridRef}
            modules={[SetFilterModule]}
            rowData={metricsData || []}
            columnDefs={metricsColumnDefs}
            defaultColDef={defaultColDef}
            isLoading={tableLoading}
            loadingMessage="Loading metrics data..."
            showToolbar={true}
            title="Metrics Data"
            showExport={true}
            exportLabel="Export to Excel"
            exportConfig={{ fileName: `metrics_${formData.cusip}_${dayjs().format('YYYY-MM-DD')}.xlsx`, sheetName: 'Metrics' }}
            customToolbarComponents={metricsToolbarComponents}
            onGridReady={metricsGridConfigs.onGridReady}
            onFilterChanged={metricsGridConfigs.onGridStateChanged}
            agGridProps={metricsGridConfigs.stashBindings}
            height={550}
            showQuickFilter={false}
            showFooter={false}
            copyHeadersToClipboard={true}
            suppressHorizontalScroll={false}
            alwaysShowHorizontalScroll={true}
          />
        </Paper>
      )}
    </>
  );
};

export default CusipTables;